import { type JSX, type KeyboardEvent, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

import { pt } from '../copy';

export type PanoramaFrame = { id: string; src: string; year: number };

export type PanoramaViewerProps = {
  frames: readonly PanoramaFrame[];
  blendT: number;
  onError?: (err: Error) => void;
  className?: string;
  hint?: string;
};

type ViewerRefs = {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  outerGeometry: THREE.SphereGeometry;
  innerGeometry: THREE.SphereGeometry;
  outerMaterial: THREE.MeshBasicMaterial;
  innerMaterial: THREE.MeshBasicMaterial;
  outerMesh: THREE.Mesh;
  innerMesh: THREE.Mesh;
};

const MIN_PITCH = -Math.PI / 2 + 0.05;
const MAX_PITCH = Math.PI / 2 - 0.05;
const MAX_TEXTURE_CACHE = 12;
const MIN_FOV = 30;
const MAX_FOV = 90;
const DEFAULT_FOV = 70;
const keyboardFocusableProps = { tabIndex: 0 } as const;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export default function PanoramaViewer({
  frames,
  blendT,
  onError,
  className,
  hint,
}: PanoramaViewerProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<ViewerRefs | null>(null);
  const textureCacheRef = useRef(new Map<string, THREE.Texture>());
  const pendingTextureLoadsRef = useRef(new Set<string>());
  const validTextureKeysRef = useRef(new Set<string>());
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const rafRef = useRef<number | null>(null);
  const yawRef = useRef(0);
  const pitchRef = useRef(0);
  const activePointerIdRef = useRef<number | null>(null);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
  const currentFramesRef = useRef<{ outer: PanoramaFrame | null; inner: PanoramaFrame | null }>({
    outer: null,
    inner: null,
  });
  const onErrorRef = useRef(onError);
  const isDisposedRef = useRef(false);
  const [hintDismissed, setHintDismissed] = useState(false);

  onErrorRef.current = onError;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.domElement.style.display = 'block';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.width = '100%';

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(DEFAULT_FOV, 1, 0.1, 1000);
    camera.position.x = 0;
    camera.position.y = 0;
    camera.position.z = 0;

    const outerGeometry = new THREE.SphereGeometry(500, 64, 40);
    const innerGeometry = new THREE.SphereGeometry(499, 64, 40);
    const outerMaterial = new THREE.MeshBasicMaterial({
      side: THREE.BackSide,
      transparent: true,
      depthWrite: false,
    });
    const innerMaterial = new THREE.MeshBasicMaterial({
      side: THREE.BackSide,
      transparent: true,
      depthWrite: false,
    });
    const outerMesh = new THREE.Mesh(outerGeometry, outerMaterial);
    const innerMesh = new THREE.Mesh(innerGeometry, innerMaterial);

    scene.add(outerMesh);
    scene.add(innerMesh);
    container.appendChild(renderer.domElement);
    isDisposedRef.current = false;

    viewerRef.current = {
      renderer,
      scene,
      camera,
      outerGeometry,
      innerGeometry,
      outerMaterial,
      innerMaterial,
      outerMesh,
      innerMesh,
    };

    const refreshSize = () => {
      const width = Math.max(container.clientWidth, 1);
      const height = Math.max(container.clientHeight, 1);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };

    const renderFrame = () => {
      const cosPitch = Math.cos(pitchRef.current);
      const targetX = Math.sin(yawRef.current) * cosPitch;
      const targetY = Math.sin(pitchRef.current);
      const targetZ = Math.cos(yawRef.current) * cosPitch;

      camera.lookAt(targetX, targetY, targetZ);
      renderer.render(scene, camera);
      rafRef.current = window.requestAnimationFrame(renderFrame);
    };

    const handlePointerDown = (event: PointerEvent) => {
      activePointerIdRef.current = event.pointerId;
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
      container.setPointerCapture(event.pointerId);
      setHintDismissed(true);
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (activePointerIdRef.current !== event.pointerId) return;

      const lastPointer = lastPointerRef.current;
      const deltaX = event.movementX || (lastPointer ? event.clientX - lastPointer.x : 0);
      const deltaY = event.movementY || (lastPointer ? event.clientY - lastPointer.y : 0);

      yawRef.current -= deltaX * 0.0025;
      pitchRef.current = clamp(pitchRef.current - deltaY * 0.0025, MIN_PITCH, MAX_PITCH);
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
    };

    const releasePointer = (event: PointerEvent) => {
      if (activePointerIdRef.current !== event.pointerId) return;
      if (container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId);
      }
      activePointerIdRef.current = null;
      lastPointerRef.current = null;
    };

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      camera.fov = clamp(camera.fov + event.deltaY * 0.05, MIN_FOV, MAX_FOV);
      camera.updateProjectionMatrix();
    };

    const previousTouchAction = container.style.touchAction;
    container.style.touchAction = 'none';

    resizeObserverRef.current = new ResizeObserver(refreshSize);
    resizeObserverRef.current.observe(container);
    container.addEventListener('pointerdown', handlePointerDown);
    container.addEventListener('pointermove', handlePointerMove);
    container.addEventListener('pointerup', releasePointer);
    container.addEventListener('pointercancel', releasePointer);
    container.addEventListener('wheel', handleWheel, { passive: false });

    refreshSize();
    renderFrame();

    return () => {
      isDisposedRef.current = true;

      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;

      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointermove', handlePointerMove);
      container.removeEventListener('pointerup', releasePointer);
      container.removeEventListener('pointercancel', releasePointer);
      container.removeEventListener('wheel', handleWheel);
      container.style.touchAction = previousTouchAction;

      outerGeometry.dispose();
      innerGeometry.dispose();
      outerMaterial.dispose();
      innerMaterial.dispose();

      textureCacheRef.current.forEach((texture) => {
        texture.dispose();
      });
      textureCacheRef.current.clear();
      pendingTextureLoadsRef.current.clear();
      validTextureKeysRef.current.clear();

      renderer.dispose();
      renderer.domElement.remove();

      viewerRef.current = null;
      currentFramesRef.current = { outer: null, inner: null };
      activePointerIdRef.current = null;
      lastPointerRef.current = null;
    };
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const viewer = viewerRef.current;
    if (!viewer) {
      return;
    }

    let handled = true;

    switch (event.key) {
      case 'ArrowLeft':
        yawRef.current -= 0.1;
        break;
      case 'ArrowRight':
        yawRef.current += 0.1;
        break;
      case 'ArrowUp':
        pitchRef.current = clamp(pitchRef.current + 0.1, MIN_PITCH, MAX_PITCH);
        break;
      case 'ArrowDown':
        pitchRef.current = clamp(pitchRef.current - 0.1, MIN_PITCH, MAX_PITCH);
        break;
      case '+':
      case '=':
        viewer.camera.fov = clamp(viewer.camera.fov - 2, MIN_FOV, MAX_FOV);
        viewer.camera.updateProjectionMatrix();
        break;
      case '-':
      case '_':
        viewer.camera.fov = clamp(viewer.camera.fov + 2, MIN_FOV, MAX_FOV);
        viewer.camera.updateProjectionMatrix();
        break;
      case '0':
        yawRef.current = 0;
        pitchRef.current = 0;
        viewer.camera.fov = DEFAULT_FOV;
        viewer.camera.updateProjectionMatrix();
        break;
      case 'Escape':
        event.currentTarget.blur();
        break;
      default:
        handled = false;
        break;
    }

    if (!handled) {
      return;
    }

    event.preventDefault();
    setHintDismissed(true);
  };

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const touchTexture = (key: string, texture: THREE.Texture) => {
      const cache = textureCacheRef.current;
      cache.delete(key);
      cache.set(key, texture);
    };

    const evictTextureCache = () => {
      const cache = textureCacheRef.current;
      const protectedKeys = new Set(
        [currentFramesRef.current.outer?.src, currentFramesRef.current.inner?.src].filter(
          (value): value is string => Boolean(value),
        ),
      );

      while (cache.size > MAX_TEXTURE_CACHE) {
        const oldestKey = cache.keys().next().value;
        if (typeof oldestKey !== 'string') {
          break;
        }

        if (protectedKeys.has(oldestKey)) {
          const texture = cache.get(oldestKey);
          if (!texture) {
            cache.delete(oldestKey);
            continue;
          }

          cache.delete(oldestKey);
          cache.set(oldestKey, texture);
          continue;
        }

        const texture = cache.get(oldestKey);
        cache.delete(oldestKey);
        texture?.dispose();
      }
    };

    const pruneStaleTextures = () => {
      const validTextureKeys = validTextureKeysRef.current;
      const cache = textureCacheRef.current;

      for (const [key, texture] of cache) {
        if (validTextureKeys.has(key)) {
          continue;
        }

        cache.delete(key);
        texture.dispose();
      }
    };

    validTextureKeysRef.current = new Set(frames.map((frame) => frame.src));
    pruneStaleTextures();

    const assignCachedTextures = () => {
      const outerFrame = currentFramesRef.current.outer;
      const innerFrame = currentFramesRef.current.inner;
      const outerTexture = outerFrame
        ? (textureCacheRef.current.get(outerFrame.src) ?? null)
        : null;
      const innerTexture = innerFrame
        ? (textureCacheRef.current.get(innerFrame.src) ?? null)
        : null;

      if (outerFrame && outerTexture) {
        touchTexture(outerFrame.src, outerTexture);
      }
      if (innerFrame && innerTexture) {
        touchTexture(innerFrame.src, innerTexture);
      }

      viewer.outerMaterial.map = outerTexture;
      viewer.innerMaterial.map = innerTexture;
      viewer.outerMaterial.needsUpdate = true;
      viewer.innerMaterial.needsUpdate = true;
    };

    const requestTexture = (frame: PanoramaFrame) => {
      if (textureCacheRef.current.has(frame.src) || pendingTextureLoadsRef.current.has(frame.src)) {
        return;
      }

      pendingTextureLoadsRef.current.add(frame.src);

      const loader = new THREE.TextureLoader();
      loader.setCrossOrigin('anonymous');

      loader.load(
        frame.src,
        (texture) => {
          pendingTextureLoadsRef.current.delete(frame.src);
          texture.colorSpace = THREE.SRGBColorSpace;

          if (isDisposedRef.current || !validTextureKeysRef.current.has(frame.src)) {
            texture.dispose();
            return;
          }

          touchTexture(frame.src, texture);
          evictTextureCache();

          if (!isDisposedRef.current) {
            assignCachedTextures();
          }
        },
        undefined,
        () => {
          pendingTextureLoadsRef.current.delete(frame.src);
          onErrorRef.current?.(
            new Error(`Failed to load panorama texture for frame "${frame.id}" from ${frame.src}`),
          );
        },
      );
    };

    if (frames.length === 0) {
      currentFramesRef.current = { outer: null, inner: null };
      viewer.outerMaterial.opacity = 0;
      viewer.innerMaterial.opacity = 0;
      assignCachedTextures();
      return;
    }

    const maxIndex = frames.length - 1;
    const clampedBlend = clamp(blendT, 0, 1);
    const timelinePosition = clampedBlend * maxIndex;
    const fromIdx = clamp(Math.floor(timelinePosition), 0, maxIndex);
    const toIdx = Math.min(fromIdx + 1, maxIndex);
    const localT = timelinePosition - fromIdx;
    const outerFrame = frames[fromIdx] ?? null;
    const innerFrame = frames[toIdx] ?? null;

    currentFramesRef.current = { outer: outerFrame, inner: innerFrame };
    viewer.outerMaterial.opacity = 1 - localT;
    viewer.innerMaterial.opacity = localT;

    assignCachedTextures();

    if (outerFrame) requestTexture(outerFrame);
    if (innerFrame) requestTexture(innerFrame);
  }, [blendT, frames]);

  return (
    <div
      {...keyboardFocusableProps}
      ref={containerRef}
      aria-label={pt.viewerAriaLabel}
      className={[
        'relative h-full w-full overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-black',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role="application"
      onKeyDown={handleKeyDown}
    >
      {hint ? (
        <div
          className={[
            'pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full',
            'bg-black/45 px-3 py-1.5 text-xs tracking-wide text-white/85 backdrop-blur-sm',
            'transition-opacity duration-[600ms]',
            hintDismissed ? 'opacity-0' : 'opacity-100',
          ].join(' ')}
        >
          {hint}
        </div>
      ) : null}
    </div>
  );
}
